import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Formik } from 'formik';
import _ from 'lodash';
import * as Yup from 'yup';

import { booleanOptionsForRadioGroup } from '../../../../../features/forms/helpers/random-form-helpers';

import {
  closeDangrousGoodsDropdown,
  searchDangerousGoods,
  selectDangerousGoods,
  showDangerousGoodsModal,
  updateDangerousGoods,
} from '../../../slices/portCallSlice';

import {
  initialFormDangerousGoodsValues,
  StowageLevelList,
  StowagePositionList,
  TableColumns,
  TypeOfCargoList,
} from './config';
import Input from '../../../../forms/components/Input';
import MultiSelect from '../../../../forms/components/MultiSelect';
import RadioButtonsCustom from '../../../../forms/components/RadioButtonsCustom';
import { removeNullUndefined } from '../../../../global/utils/cleanEmptyData';
import { validateSchema } from '../../../../global/utils/validateForms';
import Button from '../../../../layout/components/Button';
import ConfirmationModal from '../../../../layout/components/ConfirmationModal';
import Modal from '../../../../layout/components/Modal';
import ProgressBar from '../../../../layout/components/ProgressBar';
import ConfirmDeleteModal from '../../../../modals/components/ConfirmDeleteModal';
import Table from '../../../../tables/components/Table';

import plusIcon from '../../../../tables/styles/images/plusIcon.svg';

import '../styles/dangerous-goods-table.scss';

const marinePollutantOptions = booleanOptionsForRadioGroup();

function DangerousGoodsTable() {
  const dispatch = useDispatch();
  const dangerousGoods = useSelector(selectDangerousGoods);
  const shouldShowDangerousGoodsDropdown = dangerousGoods.ui.showDropdown;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isTanker, setIsTanker] = useState(false);
  const [initialValues, setInitialValues] = useState(
    initialFormDangerousGoodsValues
  );

  const handleDangerousGoodsUnChanged = _.debounce((value) => {
    if (value) {
      dispatch(
        searchDangerousGoods({
          un: value,
        })
      );
    }
  }, 500);

  const handleDangerousGoodsUnChangeDebounced = ({ target: { value } }) => {
    if (handleDangerousGoodsUnChanged.cancel) {
      handleDangerousGoodsUnChanged.cancel(); // If there is an existing debounce, cancel it
    }

    handleDangerousGoodsUnChanged(value); // Start a new debounce with the latest value
  };

  const dangerousGoodsSearchResults =
    dangerousGoods.dangerousGoodsSearchResults;
  const shouldShowLoadingInDangerousGoodsDropdown =
    dangerousGoods.dangerousGoodsSearchResultsLoading;
  const dangerousGoodsDropdownOptions = dangerousGoodsSearchResults;

  const validationSchema = Yup.object().shape({
    typeOfCargo: Yup.string().required('*This field is required'),
    un: Yup.string().required('*This field is required'),
    properShippingName: Yup.string().when('un', {
      is: (value) => {
        return !!value && value.trim().length > 0;
      },
      then: () => Yup.string().required('*This field is required'),
      otherwise: () => Yup.string().nullable(),
    }),
    class: Yup.string().when('un', {
      is: (value) => {
        return !!value && value.trim().length > 0;
      },
      then: () => Yup.string().required('*This field is required'),
      otherwise: () => Yup.string().nullable(),
    }),
    marinePollutant: Yup.string().required('*This field is required'),
    netWeight: Yup.string().required('*This field is required'),
    ems: Yup.string()
      .matches(/^F-[A-J]{1}S-[A-Z]{1}$/, 'Invalid format')
      .nullable(),
    tankHoldNumber: Yup.string()
      .matches(/^[A-Za-z0-9,]{0,25}$/g, 'Invalid format')
      .nullable(),
  });

  const formatFormValuesForApi = (values) => {
    return removeNullUndefined({
      ...values,
      typeOfCargo: values?.typeOfCargo,
      stowagePosition:
        values?.typeOfCargo !== 'Liquid bulk' ? values?.stowagePosition : null,
      stowageLevel:
        values?.typeOfCargo !== 'Liquid bulk' ? values?.stowageLevel : null,
      stowageTankerNumber: values?.tankHoldNumber,
      stowagePositionHold: values?.tankHoldNumber,
      grossWeight: values?.grossWeight,
      netWeight: values?.netWeight,
      volume: values?.volume,
      ems: values?.ems,
      specialProvisions: values?.specialProvisions,
      packingInstructions: values?.packingInstructions,
      additionalInformation: values?.additionalInformation,
      subsidiaryRisk: values?.subsidiaryRisk,
      subsidiaryRisk2: values?.subsidiaryRisk2,
      un: values?.un,
      packingGroup: values?.packingGroup,
      class: values?.class,
      properShippingName: values?.properShippingName,
    });
  };

  const formatFormValuesFromApi = async (values) => {
    return removeNullUndefined({
      ...values,
      tankHoldNumber:
        values?.stowageTankerNumber || values?.stowagePositionHold,
      typeOfCargo:
        TypeOfCargoList?.find((el) => el.value === values?.typeOfCargo)
          ?.value || null,
      stowagePosition:
        StowagePositionList?.find((el) => el.value === values?.stowagePosition)
          ?.value || null,
      stowageLevel:
        StowageLevelList?.find((el) => el.value === values?.stowageLevel)
          ?.value || null,
      grossMass: values?.massOrVolume?.split(' ')[0] || null,
      netVolume: values?.massOrVolume?.split(' ')[1] || null,
    });
  };

  const handleSubmit = async (values) => {
    const formattedValues = formatFormValuesForApi(values);
    if (isEditingRow) {
      const newMembers = dangerousGoods.dangerousGoodsList.map((member, i) =>
        i === editingRow ? formattedValues : removeNullUndefined(member)
      );
      dispatch(updateDangerousGoods({ dangerousGoods: newMembers }));
    } else {
      dispatch(
        updateDangerousGoods({
          dangerousGoods: dangerousGoods.dangerousGoodsList
            .map((element) => removeNullUndefined(element))
            .concat(formattedValues),
        })
      );
    }
    setEditingRow(null);
    setIsModalOpen(false);
    setInitialValues(initialFormDangerousGoodsValues);
  };

  const [isEditingTable, setIsEditingTable] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  let isEditingRow = editingRow !== null;

  useEffect(() => {
    isEditingRow = editingRow !== null;
    const row = isEditingRow
      ? dangerousGoods.dangerousGoodsList[editingRow]
      : null;
    if (row)
      formatFormValuesFromApi(row).then((result) => {
        setIsTanker(result?.typeOfCargo === 'Liquid bulk');
        setInitialValues(result);
      });
  }, [editingRow]);

  const [confirmDeleteMemberModalData, setConfirmDeleteMemberModalData] =
    useState({
      index: null,
      showModal: false,
    });
  const [
    showConfirmDeleteAllMembersModal,
    setShowConfirmDeleteAllMembersModal,
  ] = useState(false);

  const handleDangerousGoodClicked = function (dangerousGood) {
    dispatch(closeDangrousGoodsDropdown());

    setInitialValues({
      ...initialValues,
      class: dangerousGood?.data?.class || '',
      un: dangerousGood?.data?.un || '',
      properShippingName: dangerousGood?.data?.name || '',
      packingGroup: dangerousGood?.data?.packingGroup || '',
    });
  };

  const convertDataForTable = (data) => {
    return (
      data?.map((el) => ({
        ...el,
        stowagePosition:
          StowagePositionList.find(
            (element) => element.value === el.stowagePosition
          )?.label || '',
        subsidiaryRisk: `${el?.marinePollutant ? 'P' : ''} ${el?.subsidiaryRisk || ''} ${el?.subsidiaryRisk2 || ''} ${
          el?.subsidiaryRisk3 || ''
        }`,
        massOrVolume: `${el?.netWeight ? 'M:' + el?.netWeight : ''} ${el?.volume ? 'V:' + el?.volume : ''}`,
        bookingNumber: `${el?.bookingNumber ? el?.bookingNumber : ''} ${
          el?.billOfLadingNumber ? el?.billOfLadingNumber : ''
        }`,
      })) || []
    );
  };

  return (
    <div className="dangerous-goods-table">
      <Table
        title={'Dangerous goods manifest'}
        addButtonText={'Add dangerous goods'}
        data={convertDataForTable(dangerousGoods.dangerousGoodsList)}
        columns={TableColumns}
        editingMode={isEditingTable}
        setEditingMode={setIsEditingTable}
        setIsAddModalOpen={setIsModalOpen}
        setIsDeleteModalOpen={setShowConfirmDeleteAllMembersModal}
        onEditButtonClick={(rowIndex) => {
          setEditingRow(rowIndex);
          setIsModalOpen(true);
        }}
        onDeleteButtonClick={(rowIndex) => {
          setConfirmDeleteMemberModalData({
            showModal: true,
            index: rowIndex,
          });
        }}
      />
      <Modal
        className={`add-dangerous-good-modal`}
        title={'Dangerous goods manifest'}
        onClick={() => {
          dispatch(closeDangrousGoodsDropdown());
        }}
        showModal={isModalOpen}
        onCloseModal={() => {
          dispatch(closeDangrousGoodsDropdown());
          setIsModalOpen(false);
          setEditingRow(null);
          setInitialValues(initialFormDangerousGoodsValues);
        }}>
        <ConfirmationModal
          showModal={isConfirmationModalOpen}
          handleCloseModal={setIsConfirmationModalOpen}
          willDisableSave={formValues}
          onAcceptModal={() => {
            setIsConfirmationModalOpen(false);
            setIsModalOpen(false);
            handleSubmit(formValues);
          }}
          className=""
        />
        <div className="add-dangerous-good-modal__content">
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
            validate={(values) => {
              values = {
                ...values,
                ems: values?.ems ? values?.ems : null,
              };
              const errors = validateSchema(
                values,
                validationSchema,
                setFormValues,
                setIsConfirmationModalOpen,
                setIsModalOpen
              );
              return errors;
            }}>
            {({ values }) => (
              <Form className="add-dangerous-good-form">
                <ProgressBar percentage={100} pageNumber={1} totalPages={1} />
                {
                  <>
                    <div className="add-dangerous-good-form__row">
                      <MultiSelect
                        name="typeOfCargo"
                        options={TypeOfCargoList}
                        isMulti={false}
                        onChange={(value) => {
                          setInitialValues({
                            ...initialValues,
                            typeOfCargo: value.value,
                          });
                          if (value.value === 'Liquid bulk') {
                            setIsTanker(true);
                            setInitialValues({
                              ...initialValues,
                              typeOfCargo: value.value,
                              stowagePosition: null,
                              stowageLevel: null,
                            });
                          } else {
                            setIsTanker(false);
                          }
                        }}
                        label="Type of cargo*"
                        placeholder="Select one"
                      />
                      <MultiSelect
                        name="stowagePosition"
                        options={StowagePositionList}
                        isMulti={false}
                        disabled={isTanker}
                        onChange={(value) => {
                          setInitialValues({
                            ...initialValues,
                            stowagePosition: isTanker ? null : value.value,
                          });
                        }}
                        label="Stowage position"
                        placeholder="Select one"
                      />
                      <MultiSelect
                        name="stowageLevel"
                        options={StowageLevelList}
                        isMulti={false}
                        disabled={isTanker}
                        onChange={(value) => {
                          setInitialValues({
                            ...initialValues,
                            stowageLevel: isTanker ? null : value.value,
                          });
                        }}
                        label="Stowage level"
                        placeholder="Select one"
                      />
                      <Input
                        name="tankHoldNumber"
                        onChange={(e) => {
                          setInitialValues({
                            ...initialValues,
                            tankHoldNumber: e.target.value,
                          });
                        }}
                        placeholder="Ex: 1S,2S,3S"
                        label={'Tank or hold number'}
                      />
                    </div>
                    <hr />
                    <div className="add-dangerous-good-form__row">
                      <div className="un-container">
                        <Input
                          name="un"
                          onClick={(e) => {
                            if (dangerousGoodsSearchResults.length) {
                              e.stopPropagation();
                              dispatch(showDangerousGoodsModal());
                            }
                          }}
                          onChange={handleDangerousGoodsUnChangeDebounced}
                          placeholder="UN Number"
                          label="UN Number*"
                        />
                        {shouldShowDangerousGoodsDropdown ? (
                          <div className="list">
                            {shouldShowLoadingInDangerousGoodsDropdown
                              ? 'Loading..'
                              : null}
                            {dangerousGoodsSearchResults.length === 0 && (
                              <span className="un__empty-case">
                                Didn&apos;t find any dangerous goods with that
                                UN code
                              </span>
                            )}
                            {dangerousGoodsDropdownOptions.map(
                              (dangerousGood, index) => {
                                return (
                                  <div
                                    key={index}
                                    className="list__item"
                                    onClick={() =>
                                      handleDangerousGoodClicked(dangerousGood)
                                    }>
                                    {dangerousGood.label}
                                  </div>
                                );
                              }
                            )}
                          </div>
                        ) : null}
                      </div>
                      <Input
                        name="packingGroup"
                        disabled={!values?.un}
                        label="Packing group"
                        placeholder="Packing group"
                      />
                      <Input
                        name="properShippingName"
                        disabled={!values?.un}
                        label="Proper shipping name / Techincal specifications*"
                        placeholder="Proper shipping name / Techincal specifications"
                      />
                      <Input
                        name="class"
                        disabled={!values?.un}
                        label="Class*"
                        placeholder="Class"
                      />
                    </div>
                    <div className="add-dangerous-good-form__row">
                      <Input
                        name="subsidiaryRisk"
                        placeholder="Type One"
                        label="Subsidiary Risk 1 (if any)"
                      />
                      <Input
                        name="subsidiaryRisk2"
                        placeholder="Type One"
                        label="Subsidiary Risk 2 (if any)"
                      />
                      <RadioButtonsCustom
                        name="marinePollutant"
                        label="Marine Pollutant*"
                        options={marinePollutantOptions}
                      />
                      <Input
                        name="flashPoint"
                        placeholder="Flash Point (&deg;C)"
                        label="Flash Point (&deg;C)"
                        type={'number'}
                        min={0}
                      />
                    </div>
                    <div className="add-dangerous-good-form__row">
                      <Input
                        name="netWeight"
                        placeholder="Net Mass (Kg)"
                        label="Net Mass (Kg)*"
                        type={'number'}
                        min={0}
                      />
                      <Input
                        name="grossWeight"
                        placeholder="Gross mass (Kg)"
                        label="Gross mass (Kg)"
                        type={'number'}
                        min={0}
                      />
                      <Input
                        name="volume"
                        placeholder="Net Volume (m3)"
                        label="Net Volume (m3)"
                        type={'number'}
                        min={0}
                      />
                      <Input name="ems" placeholder="Ex: F-JS-K" label="EmS" />
                    </div>
                    <div className="add-dangerous-good-form__row">
                      <Input
                        name="specialProvisions"
                        placeholder="Special Provisions"
                        label="Special Provisions"
                      />
                      <Input
                        name="packingInstructions"
                        placeholder="Packing Instructions"
                        label="Packing Instructions"
                      />
                      <Input
                        name="additionalInformation"
                        placeholder="Addition information"
                        label="Addition information (if any)"
                      />
                    </div>
                    <div className="add-dangerous-good__form-submission-btns">
                      <Button
                        className="discard-btn"
                        onClick={() => {
                          setIsModalOpen(false);
                          setTimeout(() => setEditingRow(null), 300);
                        }}>
                        Discard
                      </Button>
                      {isEditingRow ? (
                        <Button type={'submit'}>Change Information</Button>
                      ) : (
                        <Button iconLeft={plusIcon} type="submit">
                          Add
                        </Button>
                      )}
                    </div>
                  </>
                }
              </Form>
            )}
          </Formik>
        </div>
      </Modal>
      <ConfirmDeleteModal
        title={
          <h3>
            Are you sure you want to{' '}
            <span>
              delete all <br />
              dangerous goods
            </span>{' '}
            from the list?
          </h3>
        }
        show={showConfirmDeleteAllMembersModal}
        onCancelClick={() => setShowConfirmDeleteAllMembersModal(false)}
        onDeleteClick={() => {
          setShowConfirmDeleteAllMembersModal(false);
          dispatch(
            updateDangerousGoods({
              dangerousGoods: [],
            })
          );
        }}
        onCloseModal={() => setShowConfirmDeleteAllMembersModal(false)}
      />
      <ConfirmDeleteModal
        title={<h3>Are you sure you want to delete this dangerous good?</h3>}
        show={confirmDeleteMemberModalData.showModal}
        onCancelClick={() => {
          setConfirmDeleteMemberModalData({
            index: null,
            showModal: false,
          });
        }}
        onDeleteClick={() => {
          const newDangerousGoods = [...dangerousGoods.dangerousGoodsList];
          newDangerousGoods.splice(confirmDeleteMemberModalData.index, 1);
          dispatch(
            updateDangerousGoods({
              dangerousGoods: newDangerousGoods,
            })
          );

          setConfirmDeleteMemberModalData({
            index: null,
            showModal: false,
          });
        }}
        onCloseModal={() => setShowConfirmDeleteAllMembersModal(false)}
      />
    </div>
  );
}

export default DangerousGoodsTable;
